import React from 'react'
import { Box, Typography } from "@mui/material"
import { AutocompleteAjaxSeeker, IconSeeker, FlagsIconSeeker } from "@ui-seeker/core"
import { translateCountryName } from "@ui-seeker/utils"
import { useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"
import { BACKEND_APIS } from "@constants"
import { AppAsyncOfficesAutocomplete, AppBulkEditAction, AppBulkEditDialog, AppSitesAutocomplete, AppProvidersAutocomplete } from "@components"
import { bulkFieldModal, acrissTable, categoriesTable } from "Pages/User/CustomMapping/@signals"
import { MAPPING_TYPE } from "Pages/User/CustomMapping/@constants"
import { useToast } from "@hooks"

// the same icons as the selection bar buttons that open it
const FIELD_ICONS = {
	countries: 'flag',
	offices: 'building',
	sites: 'globe-pointer',
	providers: 'handshake',
}

const FIELD_LABELS = {
	countries: 'Countries',
	offices: 'Offices',
	sites: 'Sites',
	providers: 'Providers',
}

export default function BulkFieldModal({ path }) {
	const modal = bulkFieldModal.value
	const isOpen = !!modal?.field && modal?.path === path
	const field = modal?.field
	const rows = modal?.rows || []
	const values = useSignal([])
	const isLoading = useSignal(false)
	const { showToast } = useToast()

	const affectedRows = field === 'countries'
		? rows.filter(r => !r.idOffices?.length)
		: field === 'offices'
		? rows.filter(r => !r.idCountries?.length)
		: rows

	const skippedCount = (field === 'countries' || field === 'offices') ? rows.length - affectedRows.length : 0

	const handleClose = () => {
		bulkFieldModal.value = null
		values.value = []
	}

	const handleAction = async (action) => {
		if (!values.value?.length || !affectedRows.length) return

		const api = path === MAPPING_TYPE.ACRISS ? BACKEND_APIS.CUSTOM_ACRISS : BACKEND_APIS.CUSTOM_CATEGORY
		const idCustom = affectedRows.map(r => r.idCustom)

		isLoading.value = true
		try {
			const updated = await postSubmit(`${api}/bulk-update`, { idCustom, field, action, values: values.value })

			const tableSignal = path === MAPPING_TYPE.ACRISS ? acrissTable : categoriesTable
			const updatedMap = Object.fromEntries(updated.map(r => [r.idCustom, r]))
			tableSignal.value = tableSignal.value.map(r => updatedMap[r.idCustom] || r)

			handleClose()
		} catch {
			showToast.error('Error updating mappings')
		} finally {
			isLoading.value = false
		}
	}

	const isDisabled = !values.value?.length || !affectedRows.length

	return <AppBulkEditDialog
		open={isOpen}
		onClose={handleClose}
		icon={FIELD_ICONS[field]}
		title={`Edit ${FIELD_LABELS[field]} — ${affectedRows.length} mapping${affectedRows.length !== 1 ? 's' : ''}`}
		subtitle={skippedCount > 0 ? `(${skippedCount} skipped — have specific offices)` : undefined}
		maxWidth="sm"
		actions={<>
			<AppBulkEditAction
				danger
				disabled={isDisabled}
				loading={isLoading.value}
				onClick={() => handleAction('remove')}
				startIcon={<IconSeeker icon="trash-can"/>}
			>
				Remove
			</AppBulkEditAction>
			<AppBulkEditAction
				disabled={isDisabled}
				loading={isLoading.value}
				onClick={() => handleAction('add')}
				startIcon={<IconSeeker icon="plus"/>}
			>
				Add
			</AppBulkEditAction>
		</>}
	>
		{field === 'countries' && <AutocompleteAjaxSeeker
			multiple
			filterSelectedOptions={false}
			label="Countries"
			placeholder="Select countries..."
			urlAjax={`${BACKEND_APIS.MASTER_DATA}/options/office-countries`}
			value={values.value}
			onChange={e => { values.value = e.target.value }}
			formatOptionLabel={({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<FlagsIconSeeker countryName={value}/>
				<Typography>{translateCountryName(value)}</Typography>
			</Box>}
		/>}
		{field === 'offices' && <AppAsyncOfficesAutocomplete
			multiple
			value={values.value}
			onChange={e => { values.value = e.target.value }}
		/>}
		{field === 'sites' && <AppSitesAutocomplete
			value={values.value}
			onChange={e => { values.value = e.target.value }}
		/>}
		{field === 'providers' && <AppProvidersAutocomplete
			value={values.value}
			onChange={e => { values.value = e.target.value }}
		/>}
	</AppBulkEditDialog>
}
